import React, { useEffect, useMemo, useState } from 'react'
import { flowResult } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useIntl } from 'react-intl'
import { DefaultContentBody } from 'dc-delivery-sdk-js'
import { useForm } from 'react-hook-form'
import { TObservablePage } from '../../types/commerce'
import { useContentStore, useCustomerStore, useGlobalStore, useOrderStore } from '../../store/hooks/useStore'
import { PAYMENT_METHOD_IDS, CHECKOUT_ERROR_MESSAGES, DEFAULT_BONUS_CARD_TOPUP_AMOUNT } from '../../utils/constants'
import { Box, Container, Flex, Stack, Text } from '../../vanilla'
import { BonusCardIcon } from '../../components/icons'
import { CalloutMessage } from '../../components/cms/CalloutMessage'
import useNavigation from '../../hooks/use-navigation'
import { TopUpActions } from './partials/top-up-actions/top-up-actions'
import { TopUpSummary } from './partials/top-up-summary'
import { ColorPicker, TextAlignment } from '../../types/cms'
import { balanceBox } from './styles.css'
import { useLocation } from 'react-router-dom'
import { useBraintree } from '../../hooks/use-braintree'
import { PaymentProcessingOverlay } from '../../components/payment-processing-overlay'
import { ThreeDSecure, ThreeDSecureVerifyPayload } from 'braintree-web/modules/three-d-secure'
import {
  FetchPaymentMethodsPayload,
  HostedFieldsEvent,
  HostedFieldsTokenizePayload,
} from 'braintree-web'
import { PaymentSection } from '../checkout/partials/payment-section'
import {
  HostedFields as HostedFieldsInstance,
} from 'braintree-web/modules/hosted-fields'
import { Nullable } from '../../types/utils'
import { usePageDataTracking } from '../../analytics/page-tracking'
import { useBraintreeFieldsValidityWatcher } from '../account/partials/account-payment-cards/use-braintree-fields-validity-watcher'
import { useBasketStore } from '../../store/hooks/useStore'
import { useBonusCard } from '../../store/hooks/useBonusCard'
import { SubmitOrderData } from '../../types/store/basket'
import { AddressFormFields } from '../../types/forms'
import { useBraintreePaypal } from '../../hooks/use-braintree-paypal'
import { CheckoutStateContextValue } from '../../types/ui'
import { CheckoutStateContext } from '../../contexts'
import { useBraintreeGooglePay } from '../../hooks/use-braintree-googlepay'
import { OrderSubmissionData } from '../../types/payments'
import { FilteredPaymentMethod } from '../../types/store/common'
import { SummaryButtons } from './partials/top-up-summary/summary-buttons'
import { useBraintreeApplePay } from '../../hooks/use-braintree-applepay'
import { BraintreeInputsProps } from '../../types/braintree'

const BC_AVAILABILITY_PROMISE_DELIVERY_KEY = 'bonus-card-availability-promise'
const BC_PROMOTION_TOPUP_DELIVERY_KEY = 'bonus-card-top-up-message'

export const AccountBonusCardTopup: TObservablePage = observer(function AccountBonusCardTopup() {
  const { cardData, cardNumber, maxTopUpAmount } = useBonusCard(true)
  const { pathname } = useLocation()
  const navigate = useNavigation()
  const { formatNumber } = useIntl()
  const { currency, getCustomSitePreferenceById, isCustomSitePreferenceSet, customSitePreferences, filterAvailablePaymentMethods, availablePaymentMethods } =
    useGlobalStore()
  const { isRegistered, loading, topupCustomerBonusCard, customerInfo, addresses } =
    useCustomerStore()
  const { contentById } = useContentStore()
  const { bonusCardStatus } = useBasketStore()
  const [userVaultedCards, setUserVaultedCards] = useState<FetchPaymentMethodsPayload[]>([])
  const [userVaultedCardsLoaded, setUserVaultedCardsLoaded] = useState<boolean>(false)
  const [hostedFieldsLoading, setHostedFieldsLoading] = useState<boolean>(true)
  const [userData, setBraintreeUserData] = useState<string | object | undefined>()
  const [threeDS, setThreeDS] = useState<Nullable<ThreeDSecure>>(null)
  const [hostedFields, setHostedFields] = useState<Nullable<HostedFieldsInstance>>(null)
  const [braintreeFormIsValid, setBraintreeFormIsValid] = useState(false)
  const [braintreeInputs, setBraintreeInputs] = useState<BraintreeInputsProps>(
    {} as BraintreeInputsProps,
  )
  const [saveCreditCard, setSaveCreditCard] = useState<boolean>(false)

  const topUpContextValue: CheckoutStateContextValue = useMemo(() => {
    return {
      billingAddressFormValid: true,
      setBillingAddressFormValid: () => null,
      saveCreditCard,
      setSaveCreditCard,
      saveNewAddress: false,
      setSaveNewAddress: () => null,
    }
  }, [saveCreditCard])

  const {
    createBraintreeHostedFieldsNewCard,
    createBraintreeHostedFieldsVaultedCard,
    createBraintreeDataCollector,
    createBraintreeVaultInstance,
    createBraintree3DS,
  } = useBraintree()
  const { initPaypalButtons } = useBraintreePaypal()
  const { initGooglePayButtons } = useBraintreeGooglePay()
  const { initApplePayButton } = useBraintreeApplePay()
  const orderStore = useOrderStore()

  const [selectedPayment, setSelectedPayment] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [threeDSError, setThreeDSError] = useState<boolean>(false)
  const [hostedFieldsValidationErrors, setHostedFieldsValidationErrors] = useState({
    cardholderName: false,
    number: false,
    expirationDate: false,
    cvv: false,
  })
  const [paymentMethods, setPaymentMethods] = useState<Array<FilteredPaymentMethod>>([])

  const hostedFieldsErrors =
    hostedFieldsValidationErrors.cardholderName ||
    hostedFieldsValidationErrors.number ||
    hostedFieldsValidationErrors.expirationDate ||
    hostedFieldsValidationErrors.cvv

  const form = useForm<{ amount: string }>({
    defaultValues: { amount: DEFAULT_BONUS_CARD_TOPUP_AMOUNT.toFixed(2) },
    mode: 'onBlur',
  })

  const usingSavedCard = selectedPayment !== PAYMENT_METHOD_IDS.PAYPAL 
  && selectedPayment !== PAYMENT_METHOD_IDS.GOOGLE_PAY 
  && selectedPayment !== PAYMENT_METHOD_IDS.APPLE_PAY 
  && selectedPayment !== PAYMENT_METHOD_IDS.BT_CREDIT_CARD
  const lastDigits = cardNumber ? cardNumber.substring(cardNumber.length - 4) : ''
  const preferred = addresses.find((addr) => addr.preferred === true)
  const billingAddress = preferred as AddressFormFields


  let threeDSecureEnabled: boolean

  useEffect(() => {
    const setupThreeDSecurePref = async () => {
      if (!isCustomSitePreferenceSet('BRAINTREE_3DSecure_Enabled')) {
        await getCustomSitePreferenceById('BRAINTREE_3DSecure_Enabled')
      }
      threeDSecureEnabled = (customSitePreferences['BRAINTREE_3DSecure_Enabled'] ===
        'enabled') as boolean
    }
    setupThreeDSecurePref()
  })

  const setUpHostedFields = async (newCard = false) => {
    setBraintreeFormIsValid(false)

    if (hostedFields) {
      teardownHostedFields()
    }

    const defaultPaymentMethodDisabled = customSitePreferences['disableDefaultPaymentMethods']
    if (defaultPaymentMethodDisabled) {
      setHostedFieldsLoading(false)
    }

    if (userVaultedCardsLoaded && selectedPayment) {
      if (userVaultedCards.length > 0 && !newCard) {
        setHostedFieldsLoading(true)
        const btHostedFields = await createBraintreeHostedFieldsVaultedCard()
        setHostedFields(btHostedFields)
        setBraintreeFormIsValid(true)
        setHostedFieldsLoading(false)
      } else {
        setHostedFieldsLoading(true)
        const btHostedFields = await createBraintreeHostedFieldsNewCard()
        setHostedFields(btHostedFields)
        setHostedFieldsLoading(false)
      }
    }
  }

  const teardownHostedFields = async () => {
    if (hostedFields) {
      hostedFields?.off('validityChange', () => null)
      await hostedFields.teardown()
      setHostedFields(null)
    }
  }

  const handleEvent = (event: HostedFieldsEvent) => {
    if (selectedPayment === PAYMENT_METHOD_IDS.BT_CREDIT_CARD) {
      setBraintreeFormIsValid(
        event.fields['cvv'].isValid &&
        event.fields['cardholderName'].isValid &&
        event.fields['expirationDate'].isValid &&
        event.fields['number'].isValid,
      )

      setBraintreeInputs(event.fields)
    } else {
      setBraintreeFormIsValid(true)
    }
  }

  const setUpBraintreeForms = async () => {
    if (!isCustomSitePreferenceSet('disableDefaultPaymentMethods')) {
      await getCustomSitePreferenceById('disableDefaultPaymentMethods')
    }    
    if (!isCustomSitePreferenceSet('BRAINTREE_savedCardEnabled')) {
      await getCustomSitePreferenceById('BRAINTREE_savedCardEnabled')
    }

    const disabledDefaultPref = customSitePreferences['disableDefaultPaymentMethods']
    const savedCardsEnabled = customSitePreferences['BRAINTREE_savedCardEnabled']

    // Set up 3DS
    const threeDSInstance = await createBraintree3DS()
    setThreeDS(threeDSInstance)

    // Only load the vaulted Payment methods if SavedCards is enabled
    if(savedCardsEnabled) {
      // Set Up Vaulted Cards
      const vaultManagerInstance = await createBraintreeVaultInstance()
      vaultManagerInstance.fetchPaymentMethods(function (err, paymentMethods) {
        setUserVaultedCards(paymentMethods ?? [])
        setUserVaultedCardsLoaded(true)

        const defaultCard = paymentMethods?.find((method) => method.default)
        if (!disabledDefaultPref) {
          if (defaultCard) {
            setSelectedPayment(defaultCard.nonce)
          } else if (paymentMethods && paymentMethods?.length > 0) {
            setSelectedPayment(paymentMethods?.[0].nonce)
          } else {
            setSelectedPayment(PAYMENT_METHOD_IDS.BT_CREDIT_CARD)
          }
        }
      })
    } else {
      // Handle Default setup of selectedPayments when saved cards are not enabled
      setUserVaultedCards([])
      setUserVaultedCardsLoaded(true)
      if (!disabledDefaultPref) {
        setSelectedPayment(PAYMENT_METHOD_IDS.BT_CREDIT_CARD)
      }
    }

    const btUserData = await createBraintreeDataCollector()
    setBraintreeUserData(btUserData)
  }

  useEffect(() => {
    if (hostedFields) {
      hostedFields.on('validityChange', handleEvent)
      hostedFields.on('blur', handleEvent)
      hostedFields.on('focus', handleEvent)
    }
  }, [hostedFields])

  useEffect(() => {
    if (customerInfo?.customerNo) {
      setUpBraintreeForms()
    }
  }, [customerInfo?.customerNo])

  useEffect(() => {
    if (!hostedFields) {
      setUpHostedFields()
    }
  }, [userVaultedCardsLoaded])

  useEffect(() => {
    if (selectedPayment === PAYMENT_METHOD_IDS.BT_CREDIT_CARD) {
      const newCard = true
      if (!hostedFieldsLoading) {
        setUpHostedFields(newCard)
      }
    } else if (usingSavedCard) {
      const newCard = false
      if (!hostedFieldsLoading) {
        setUpHostedFields(newCard)
      }
    }
  }, [selectedPayment])

  /* Redirect user to login screen if it's not registered customer */
  useEffect(() => {
    if (!isRegistered && !loading) {
      navigate('/login', 'push', {
        directedFrom: pathname,
      })
    }
  }, [isRegistered, loading])

  const handleTopUp = async (topUpData: SubmitOrderData, paymentType: string) => {
    const result = await flowResult(
      topupCustomerBonusCard(topUpData, orderStore.topupAmount, paymentType),
    )

    if (result.success) {
      navigate('/account/bonus-card/topup/confirmation', 'push', {
        order: result,
      })
    }

    if (result?.reason === 'cvv') {
      setErrorMessage('Credit Card Verification Failed')
    } else {
      setErrorMessage(CHECKOUT_ERROR_MESSAGES.BONUSCARD_ERROR_GENERAL)
    }
    refreshHostedFields()
    setIsLoading(false)
  }

  const topUpBonusCard = async (
    threeDSData: ThreeDSecureVerifyPayload,
    braintreeData?: HostedFieldsTokenizePayload,
  ) => {
    const topUpData: SubmitOrderData = {
      btData: braintreeData ?? null,
      userData: userData as object,
      usingSavedCard,
      threeDSecureData: threeDSData ?? null,
      saveCreditCard: saveCreditCard ?? false,
    }

    await handleTopUp(topUpData, PAYMENT_METHOD_IDS.BT_CREDIT_CARD)
  }

    const handlePaymentMethodOrderSubmission =  async (...args: OrderSubmissionData) => {
      const [nonce, cardType, billingAddress, paymentMethodId, accountEmail] = args
      const topUpData: SubmitOrderData = {
        btData: {
          nonce,
          details: { cardType, accountEmail }
        },
        billingAddress
      }

      setIsLoading(true)
      
      await handleTopUp(topUpData, paymentMethodId)
    }

  const refreshHostedFields = () => {
    setUserVaultedCardsLoaded(false)
    setHostedFieldsLoading(true)
    setUpBraintreeForms()
    setHostedFieldsLoading(false)
    setIsLoading(false)
  }

  const handleThreeDSCancellation = () => {
    refreshHostedFields()
    setThreeDSError(true)
  }

  const handleThreeDSAuthentication = (savedCard?: FetchPaymentMethodsPayload, btData?: HostedFieldsTokenizePayload) => {
    return threeDS?.verifyCard({
      onLookupComplete: (data, next) => {
        next()
      },
      amount: orderStore.topupAmount,
      nonce: usingSavedCard ? savedCard?.nonce ?? '' : btData?.nonce ?? '',
      // @ts-ignore
      bin: usingSavedCard ? savedCard?.details?.bin : btData?.details.bin,// TS ignored as vaults dont work yet and `bin` doesnt exist on type. Suspect Broken/incomplete functionality
      email: customerInfo?.email,
      billingAddress: {
        givenName: preferred?.firstName,
        surname: preferred?.lastName,
        phoneNumber: preferred?.phone,
        streetAddress: preferred?.address1,
        extendedAddress: preferred?.address2,
        locality: preferred?.city,
        region: preferred?.stateCode,
        postalCode: preferred?.postcode,
        countryCodeAlpha2: preferred?.countryCode,
      },
    }).then(async (payload) => {
      const threeDSAuthenticateFailed =
        payload?.threeDSecureInfo?.status !== 'authenticate_successful'
      const threeDSCancelled =
        payload.rawCardinalSDKVerificationData?.Payment?.ExtendedData?.ChallengeCancel
      if (threeDSCancelled) {
        handleThreeDSCancellation()
      } else if (threeDSAuthenticateFailed) {
        handleThreeDSAuthenticationFailure(payload?.threeDSecureInfo?.status)
      } else {
        topUpBonusCard(payload, btData)
      }
    }).catch((error) => {
      if (error.code == 'THREEDS_CARDINAL_SDK_ERROR') {
        setIsLoading(false)
        setErrorMessage(CHECKOUT_ERROR_MESSAGES.THREEDSECURE_GENERAL)
      }
    })
  }

  const handleThreeDSAuthenticationFailure = (status: string) => {
    setIsLoading(false)
    const verificationStatus = ['lookup_error', 'lookup_failed_acs_error', 'challenge_required']
    const errorMessage = verificationStatus.includes(status)
      ? CHECKOUT_ERROR_MESSAGES.VERIFICATION_FAILED
      : CHECKOUT_ERROR_MESSAGES.THREEDSECURE_GENERAL
    setErrorMessage(errorMessage)
  }

  const tokenizeAndSubmitTopUp = async () => {
    setIsLoading(true)
    setErrorMessage(null)
    setThreeDSError(false)

    const usingSavedCard =
      selectedPayment && selectedPayment !== PAYMENT_METHOD_IDS.PAYPAL && selectedPayment !== PAYMENT_METHOD_IDS.GOOGLE_PAY && selectedPayment !== PAYMENT_METHOD_IDS.BT_CREDIT_CARD
    let savedCard: FetchPaymentMethodsPayload| undefined

    if (usingSavedCard) {
      savedCard = userVaultedCards.find((card) => card.nonce === selectedPayment)
    }

    let btData: HostedFieldsTokenizePayload | undefined

    try {
      if (usingSavedCard) {
        if (savedCard?.type === 'PayPalAccount') {
          return handlePaymentMethodOrderSubmission(savedCard?.nonce, savedCard?.type, billingAddress, PAYMENT_METHOD_IDS.PAYPAL)
        } else if (threeDSecureEnabled) {
          return handleThreeDSAuthentication(savedCard)
        }
      }
      await hostedFields?.tokenize(async (err, payload) => {
        btData = payload

        if (err || !btData) {
          setIsLoading(false)
          setErrorMessage(CHECKOUT_ERROR_MESSAGES.BONUSCARD_ERROR_GENERAL)
          console.error(err)
          throw err
        }

        if (threeDSecureEnabled) {
          return handleThreeDSAuthentication(savedCard, btData)
        }
      })
    } catch (err) {
      setIsLoading(false)
      setErrorMessage(CHECKOUT_ERROR_MESSAGES.BONUSCARD_ERROR_GENERAL)
      console.error(`ISSUE WITH BRAINTREE ${err}`)
    }
  }

  const sendPageData = usePageDataTracking()
  const pageType = 'checkout'
  const pageCategory = 'Checkout'

  useEffect(() => {
    sendPageData(pageType, pageCategory);
  }, [])

  useBraintreeFieldsValidityWatcher(hostedFields, setHostedFieldsValidationErrors)

  useEffect(() => {
    if (selectedPayment === PAYMENT_METHOD_IDS.PAYPAL) {
      initPaypalButtons({
        submitOrderProcess: handlePaymentMethodOrderSubmission,
        amount: orderStore.topupAmount,
        setErrorMessage,
        isTopUp: true,
        billingAddress: billingAddress
      })
    } else if (selectedPayment === PAYMENT_METHOD_IDS.APPLE_PAY) {
      initApplePayButton({
        amount: orderStore.topupAmount,
        setErrorMessage: setErrorMessage,
        submitOrderProcess: handlePaymentMethodOrderSubmission,
        isTopUp: true, 
        billingAddress: billingAddress
      })
    } else if (selectedPayment === PAYMENT_METHOD_IDS.GOOGLE_PAY) {
      initGooglePayButtons({
        submitOrderProcess: handlePaymentMethodOrderSubmission,
        amount: orderStore.topupAmount,
        setErrorMessage,
        isTopUp: true, 
        billingAddress: billingAddress
      })
    }
  }, [selectedPayment])

  useEffect(() => {
    (async () => {
      const filteredMethods = await filterAvailablePaymentMethods(false, false)
      setPaymentMethods(filteredMethods)
    })();
    
  }, [availablePaymentMethods])

  return (
    <CheckoutStateContext value={topUpContextValue}>
      {isLoading && <PaymentProcessingOverlay />}
      <Container
        paddingY={['24px', '60px']}
        display="flex"
        flexDirection="column"
        gap={['20px', '36px']}
        paddingX={['0px', '16px']}
      >
        <Box paddingX={['16px', '0px']}>
          <Text variant="heading5" lineHeight="short">
            Top Up My Bonus Card
          </Text>
        </Box>
        <Flex direction={['column', 'row']} gap="16px">
          <Stack
            width={['full', 'two-thirds']}
            spacing="20px"
            style={{ maxWidth: 780 }}
            flexShrink="0"
          >
            <Box bg="white" width="full">
              <Box
                borderColor="gray200"
                borderBottom="1px"
                paddingY="16px"
                paddingX={['16px', '20px']}
              >
                <Text variant="heading1" lineHeight="shorter">
                  Your Bonus Card:
                </Text>
              </Box>
              <Box paddingY="20px" paddingX="16px">
                <Flex
                  display={['flex', 'inline-flex']}
                  paddingX="12px"
                  paddingY="16px"
                  border="1px"
                  borderColor="gray200"
                  align="center"
                  width={['full', 'auto']}
                  gap="12px"
                >
                  <BonusCardIcon width="40px" height="auto" />
                  <Text variant="text4" lineHeight="shorter" data-cs-mask="">
                    Bonus Card ending {lastDigits}
                  </Text>
                </Flex>
              </Box>
              <Box paddingX="16px" paddingBottom="20px" className={balanceBox}>
                {cardData?.balance !== undefined && (
                  <Text
                    variant="unstyled"
                    fontSize="inherit"
                    lineHeight="shorter"
                    color="inherit"
                    fontWeight="inherit"
                  >
                    Current Balance: {`${formatNumber(cardData.balance, {
                      style: 'currency',
                      currency: currency,
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}`}
                  </Text>
                )}
              </Box>
            </Box>
            <Box bg="white" width="full">
              <Box
                borderColor="gray200"
                borderBottom="1px"
                paddingY="16px"
                paddingX={['16px', '20px']}
              >
                <Text variant="heading1" lineHeight="shorter">
                  Top up amount
                </Text>
              </Box>
              <Stack spacing="12px" paddingX={['16px', '20px']} paddingY="20px">
                <Text variant="text3" lineHeight="shorter">
                  How much would you like to top up?
                </Text>
                <TopUpActions
                  form={form}
                  maxTopUpAmount={maxTopUpAmount}
                  currentValue={Number(form.getValues().amount)}
                  promotionMessageContent={
                    contentById[BC_PROMOTION_TOPUP_DELIVERY_KEY] as {
                      text: string
                      alignment: TextAlignment
                    } & DefaultContentBody
                  }
                />
              </Stack>
            </Box>
            <Box bg="white" width="full">
              <Box position="relative">
                <Stack>
                  <PaymentSection
                    submitOrderProcess={tokenizeAndSubmitTopUp}
                    userVaultedCardsLoaded={userVaultedCardsLoaded}
                    bonusCardStatus={bonusCardStatus}
                    errorMessage={errorMessage}
                    userVaultedCards={userVaultedCards}
                    selectedPayment={selectedPayment}
                    setSelectedPayment={setSelectedPayment}
                    usingSavedCard={usingSavedCard}
                    braintreeInputs={braintreeInputs}
                    hostedFieldsValidationErrors={hostedFieldsValidationErrors}
                    setErrorMessage={setErrorMessage}
                    braintreeFormIsValid={braintreeFormIsValid}
                    hostedFieldsErrors={hostedFieldsErrors}
                    threeDSError={threeDSError}
                    isTopUpPage={true}
                    paymentMethods={paymentMethods}
                  />
                </Stack>
              </Box>
            </Box>
          </Stack>
          <Box bg="white" flexGrow="1" width={['full', 'auto']}>
            <Box borderColor="gray200" borderBottom="1px" padding="16px" width="full">
              <Text variant="heading1" lineHeight="shorter" data-cs-mask="">
                Bonus Card ending in {lastDigits}
              </Text>
            </Box>
            <Stack spacing="0px">
              <TopUpSummary topUpValue={orderStore.topupAmount.toFixed(2)} bcBalance={cardData?.balance ?? 0} />
              <Stack
                spacing="16px"
                width="full"
                borderBottom="1px"
                borderColor="gray200"
                padding="16px"
                style={{ maxWidth: '380px' }}
              >
                {[...userVaultedCards, ...paymentMethods].map((paymentMethod) => (
                  <SummaryButtons
                    paymentMethod={paymentMethod}
                    selectedPayment={selectedPayment}
                    isEditModeEnabled={false}
                    isTopUp={true}
                    submitOrderProcess={tokenizeAndSubmitTopUp}
                    billingAddressFormValid={true}
                    isPaymentRefunding={false}
                    braintreeFormIsValid={braintreeFormIsValid}
                    bonusCardStatus={bonusCardStatus}
                    hostedFieldsErrors={hostedFieldsErrors}
                  />
                ))}
              </Stack>
              {contentById[BC_AVAILABILITY_PROMISE_DELIVERY_KEY] && (
                <Box paddingX="16px" paddingY="20px">
                  <Box
                    padding="16px"
                    borderColor="accent3"
                    style={{ borderWidth: 4, borderStyle: 'solid' }}
                  >
                    <CalloutMessage
                      content={
                        contentById[BC_AVAILABILITY_PROMISE_DELIVERY_KEY] as {
                          text: {
                            richText: string
                          }
                          spacing: string
                          isTextWhite: boolean
                          heading: string
                          backgroundColor?: ColorPicker
                        } & DefaultContentBody
                      }
                      headingProps={{
                        color: 'accent3',
                        variant: 'unstyled',
                        fontWeight: 'extrabold',
                        style: { fontSize: 27 },
                        align: 'center',
                      }}
                      containerProps={{
                        spacing: '12px',
                      }}
                      markdownComponentProps={{
                        p: {
                          color: 'gray800',
                          align: 'center',
                        },
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Stack>
          </Box>
        </Flex>
      </Container>
    </CheckoutStateContext>
  )
})

AccountBonusCardTopup.getProps = async ({ store }) => {
  const { contentStore } = store

  if (!contentStore.contentById[BC_AVAILABILITY_PROMISE_DELIVERY_KEY])
    await flowResult(contentStore.fetchItemByKey(BC_AVAILABILITY_PROMISE_DELIVERY_KEY))

  if (!contentStore.contentById[BC_PROMOTION_TOPUP_DELIVERY_KEY])
    await flowResult(contentStore.fetchItemByKey(BC_PROMOTION_TOPUP_DELIVERY_KEY))
}
